import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { AlertTriangle, ArrowUpRight, Lock, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Button, Select, toast } from '../../components/ui';
import { cn } from '../../lib/cn';
import {
  useCreateEntry,
  useDeleteEntry,
  useSetCell,
  useUpdateEntry,
  type TimeAgreement,
  type TimeEntry,
} from './api';
import { formatCellHours, formatHoursValue, parseHoursInput, weekdayLabel, type HoursFormat } from './hours';
import { EditedByMark } from './authorship';
import { rowLabel, type RowLabel } from './labels';

/** Which cell the note editor is attached to, and what the user had typed. */
export interface CellNoteTarget {
  rowKey: string;
  /** Grid coordinates — used to put focus back in the cell on close. */
  row: number;
  col: number;
  date: string;
  /** Hours typed into the cell but not yet committed (null = use what's saved). */
  draftHours: number | null;
  anchor: HTMLElement;
}

const PANEL_WIDTH = 320;

function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export interface CellNotePopoverProps {
  target: CellNoteTarget;
  label: RowLabel;
  agreementId: string;
  taskId: string | null;
  /** Billable default for a cell that has no entry yet (the row's default). */
  rowBillable: boolean;
  entries: TimeEntry[];
  hoursFormat: HoursFormat;
  weekStart: string;
  personId: string | null;
  onClose: () => void;
  onOpenDay: (date: string) => void;
  /**
   * Where an entry may be MOVED to (logged on the wrong project). Omitted =
   * no move control. The server keeps the task when the new agreement offers
   * it and re-resolves the bill rate.
   */
  agreements?: TimeAgreement[];
}

/**
 * Edit a timesheet cell WITHOUT leaving the week.
 *
 * The note is what this app bills and reports on, so it has to be writable
 * where the hours are typed. This popover anchors to the cell and saves hours +
 * notes + billable in one call for a single-entry cell (`POST /time/cell` takes
 * all three), or patches each entry individually when the cell holds several.
 *
 * Fixed positioning is deliberate: the grid scrolls inside `overflow-x-auto`,
 * which would clip an absolutely-positioned panel.
 */
export function CellNotePopover({
  target,
  label,
  agreementId,
  taskId,
  rowBillable,
  entries,
  hoursFormat,
  weekStart,
  personId,
  onClose,
  onOpenDay,
  agreements,
}: CellNotePopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  /** How tall the panel may be where it sits — see the placement effect. */
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setCell = useSetCell(weekStart, personId);
  const updateEntry = useUpdateEntry();
  const createEntry = useCreateEntry();
  const deleteEntry = useDeleteEntry();
  const saving =
    setCell.isPending || updateEntry.isPending || createEntry.isPending || deleteEntry.isPending;

  // Immutable = billed to an invoice, or deleted in Harvest. Neither may be
  // typed over; both are shown with the reason instead of an input.
  const locked = entries.filter((e) => e.locked || e.deletedInSource);
  const unlocked = entries.filter((e) => !e.locked && !e.deletedInSource);
  const lockedHours = Math.round(locked.reduce((s, e) => s + e.hours, 0) * 100) / 100;
  const multi = unlocked.length > 1;
  const readOnly = unlocked.length === 0 && locked.length > 0;
  /** The last foreign author across the entries in this cell, if any. */
  const foreignEdit = entries.find((e) => e.editedBy)?.editedBy ?? null;

  // --- single-entry (and empty) cell: hours + notes + billable in one save ---
  const single = unlocked[0] ?? null;
  const [hoursText, setHoursText] = useState(() =>
    formatCellHours(
      target.draftHours ?? (single ? single.hours + lockedHours : lockedHours),
      hoursFormat,
    ),
  );
  const [notes, setNotes] = useState(single?.notes ?? '');
  /** Single entry: the agreement it is being moved to ('' = stays here). */
  const [moveTo, setMoveTo] = useState('');
  const moveOptions = (agreements ?? [])
    .filter((a) => a.id !== agreementId)
    .map((a) => {
      const l = rowLabel(a, null);
      return { value: a.id, label: `${l.client} — ${l.agreement}` };
    })
    .sort((x, y) => x.label.localeCompare(y.label, undefined, { numeric: true }));
  const canMove = moveOptions.length > 0;
  /** A move takes the new agreement's billable default (shown, and changeable). */
  const billableDefault = (id: string, current: boolean) => {
    const target = (agreements ?? []).find((a) => a.id === id);
    return target ? target.billBy !== 'NONE' : current;
  };
  const moveSelect = (value: string, onChange: (next: string) => void, aria: string) => (
    <Select
      selectSize="sm"
      aria-label={aria}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      options={[{ value: '', label: 'Keep on this agreement' }, ...moveOptions]}
      className="mt-1.5"
    />
  );
  const [billable, setBillable] = useState(single ? single.billable : rowBillable);
  /**
   * The billable flag as it stood when a move target was first picked (null =
   * no move pending). Picking a target applies that agreement's default;
   * "Keep on this agreement" must put back exactly this — Save sends
   * `billable` on the entry that never moved, so a leftover default would
   * quietly un-bill (or bill) it.
   */
  const [preMoveBillable, setPreMoveBillable] = useState<boolean | null>(null);

  // --- multi-entry cell: per-entry hours + notes + BILLABLE -----------------
  //
  // ⚠ `billable` used to be missing here while the single-entry branch below
  // has always had it, so a cell holding three entries offered no way to
  // un-bill one of them from the week grid at all — the only route was to
  // leave for the Day view. The flag decides whether an hour reaches an
  // invoice; it cannot be the one field that disappears when a day has more
  // than one piece of work on it.
  //
  // ⚠ A multi-entry cell could be EDITED but never grown or shrunk: three
  // entries stayed three entries, and the only route to a fourth — or to
  // removing one — was the Day view. `hours: 0` was refused with a sentence
  // that named that trip. Both now happen here, and neither is a new dialog:
  // a row is marked for removal (strikethrough, reversible until Save), and
  // "Add entry" appends a blank row saved as a new entry.
  const [drafts, setDrafts] = useState<
    Array<{
      id: string | null;
      hours: string;
      notes: string;
      billable: boolean;
      remove: boolean;
      /** Agreement this entry moves to on Save ('' = stays). */
      moveTo: string;
      /**
       * Its billable flag before a move target was picked (null = no move
       * pending) — what "Keep on this agreement" restores.
       */
      preMoveBillable: boolean | null;
    }>
  >(() =>
    unlocked.map((e) => ({
      id: e.id,
      hours: formatCellHours(e.hours, hoursFormat),
      notes: e.notes ?? '',
      billable: e.billable,
      remove: false,
      moveTo: '',
      preMoveBillable: null,
    })),
  );
  /** Rows the Save will delete — named on the button, never a surprise. */
  const removing = drafts.filter((d) => d.remove && d.id).length;
  const keeping = drafts.filter((d) => !d.remove).length;

  // Place the panel under (or above) the cell, and keep it there while the
  // page or the grid scrolls.
  useLayoutEffect(() => {
    const place = () => {
      const rect = target.anchor.getBoundingClientRect();
      const panel = panelRef.current;
      const width = panel?.offsetWidth ?? PANEL_WIDTH;
      const left = Math.max(
        8,
        Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 8),
      );
      /**
       * ⚠ **IT OPENS BELOW, AND IT SHOWS EVERY ENTRY THE ROOM ALLOWS.**
       *
       * Two rules, and the second one was missing. It must not flip up over the
       * page it is anchored to (a three-entry editor on a top row used to draw
       * straight over the KPI strip and the Week/Day control) — so it opens
       * BELOW unless the cell is genuinely near the bottom AND there is more
       * room above.
       *
       * But that ceiling was HARD, and on a dense week the grid starts only a
       * couple of hundred pixels above a lower row: a three-entry cell on Tom's
       * week of Aug 3 opened 260px tall with a **132px** list, so entries 2 and
       * 3 sat inside a silent scroller and the reader saw one of the three
       * pieces of work they had come to edit. The grid top is now a SOFT
       * ceiling — obeyed whenever the content fits under it, and given up for
       * the viewport edge only when the alternative is hiding somebody's hours.
       * Covering a KPI tile with a panel you opened and can Escape out of is a
       * smaller harm than not showing the work at all.
       *
       * `desired` is the height the content ASKS for, measured through the
       * list's own overflow rather than the panel's clipped `offsetHeight` —
       * otherwise a panel squeezed once stays squeezed for ever.
       */
      const list = panel?.querySelector<HTMLElement>('[data-note-list]') ?? null;
      const clipped = list ? Math.max(0, list.scrollHeight - list.clientHeight) : 0;
      const desired = (panel?.offsetHeight ?? 240) + clipped;
      const MARGIN = 12;
      const gridTop =
        target.anchor.closest('[aria-label="Weekly timesheet grid"]')?.getBoundingClientRect()
          .top ?? MARGIN;
      const roomBelow = window.innerHeight - rect.bottom - MARGIN;
      const insideSheet = rect.top - Math.max(MARGIN, gridTop) - 6;
      const roomAbove = desired <= insideSheet ? insideSheet : rect.top - MARGIN - 6;
      const above = roomBelow < desired && roomAbove > roomBelow;
      const room = Math.max(160, above ? roomAbove : roomBelow);
      const capped = Math.min(desired, room);
      setMaxHeight(room);
      setPos({
        top: above ? Math.max(MARGIN, rect.top - capped - 6) : rect.bottom + 6,
        left,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [target.anchor, multi, readOnly, drafts.length, error]);

  // Escape closes without writing; a click outside is a cancel too.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    const onDown = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const save = async () => {
    setError(null);
    try {
      if (multi) {
        const kept = drafts.filter((d) => !d.remove);
        const parsed = kept.map((d) => ({ ...d, value: parseHoursInput(d.hours) }));
        if (parsed.some((d) => d.value === null)) {
          setError('Enter hours as 1.5 or 1:30.');
          return;
        }
        if (parsed.some((d) => (d.value as number) <= 0)) {
          setError('An entry needs hours — remove it with the bin instead.');
          return;
        }
        const sum =
          Math.round((parsed.reduce((s, d) => s + (d.value as number), 0) + lockedHours) * 100) /
          100;
        if (sum > 24) {
          setError('A day holds at most 24h.');
          return;
        }
        if (parsed.length === 0 && drafts.length > 0) {
          setError('Removing everything empties this cell — clear it in the grid instead.');
          return;
        }
        const before = new Map(unlocked.map((e) => [e.id, e]));
        for (const d of parsed) {
          if (!d.id) {
            await createEntry.mutateAsync({
              agreementId,
              taskId,
              date: target.date,
              hours: d.value as number,
              notes: d.notes.trim() || undefined,
              billable: d.billable,
              personId: personId ?? undefined,
            });
            continue;
          }
          const was = before.get(d.id);
          if (
            was &&
            ((d.value as number) !== was.hours ||
              d.notes.trim() !== (was.notes ?? '').trim() ||
              d.billable !== was.billable ||
              d.moveTo)
          ) {
            await updateEntry.mutateAsync({
              id: d.id,
              hours: d.value as number,
              notes: d.notes.trim() ? d.notes.trim() : null,
              billable: d.billable,
              ...(d.moveTo ? { agreementId: d.moveTo } : {}),
            });
          }
        }
        // Deletions last: if an edit above fails, nothing has been destroyed.
        for (const d of drafts.filter((x) => x.remove && x.id)) {
          await deleteEntry.mutateAsync(d.id as string);
        }
      } else {
        const hours = parseHoursInput(hoursText);
        if (hours === null) {
          setError('Enter hours as 1.5 or 1:30.');
          return;
        }
        if (hours > 24) {
          setError('A day holds at most 24h.');
          return;
        }
        if (hours < lockedHours) {
          setError(
            `${formatHoursValue(lockedHours, hoursFormat)} here is billed — can't go lower.`,
          );
          return;
        }
        if (hours <= 0 && notes.trim()) {
          setError('Add hours — a note needs time to attach to.');
          return;
        }
        if (moveTo && single) {
          // A move is an edit of THIS entry, not a cell write — the entry
          // keeps its id, its note and its history on the new agreement.
          if (hours <= 0) {
            setError('Add hours — an entry needs time to move.');
            return;
          }
          await updateEntry.mutateAsync({
            id: single.id,
            hours: hours - lockedHours,
            notes: notes.trim() ? notes.trim() : null,
            billable,
            agreementId: moveTo,
          });
        } else {
          await setCell.mutateAsync({
            agreementId,
            taskId,
            date: target.date,
            hours,
            notes: notes.trim() ? notes.trim() : null,
            billable,
          });
        }
      }
      const moved = multi ? drafts.filter((d) => d.moveTo && !d.remove).length : moveTo ? 1 : 0;
      if (moved > 0) {
        toast.success(
          `${moved === 1 ? '1 entry' : `${moved} entries`} moved — ${
            moved === 1 ? 'it now sits' : 'they now sit'
          } on another row of this week.`,
        );
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Saving failed — try again.');
    }
  };

  /** ⌘/Ctrl+Enter saves from any field; plain Enter saves from the hours box. */
  const onFieldKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void save();
    }
  };

  const fieldClass =
    'w-full rounded-lg border border-grey-200 bg-white px-2.5 py-1.5 text-sm text-secondary ' +
    'placeholder:text-grey-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20';

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`Time and notes — ${label.full}, ${weekdayLabel(target.date)} ${shortDate(target.date)}`}
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: PANEL_WIDTH,
        maxHeight: maxHeight ?? undefined,
      }}
      className={cn(
        'fixed z-50 flex max-w-[calc(100vw-1rem)] flex-col rounded-xl border border-grey-200 bg-white shadow-elevated',
        pos ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div className="border-b border-grey-100 px-3 py-2">
        <p className="flex items-baseline gap-1.5 text-sm font-bold leading-tight text-secondary">
          <span className="truncate">{label.agreement}</span>
          {label.task && (
            <span className="shrink-0 truncate text-caption font-semibold text-grey-500">
              · {label.task}
            </span>
          )}
        </p>
        <p className="truncate text-caption text-grey-500">
          {label.client} · {weekdayLabel(target.date)} {shortDate(target.date)}
        </p>
        {/* ⚠ SOMEBODY ELSE WROTE THESE HOURS. This popover is where one cell is
            read and rewritten, so the author belongs at the top of it — not
            only in the week-level notice. */}
        {foreignEdit && (
          <p className="mt-1">
            <EditedByMark editedBy={foreignEdit} />
          </p>
        )}
      </div>

      <div data-note-list className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3">
        {readOnly ? (
          <div className="space-y-2">
            {locked.map((entry) => (
              <div
                key={entry.id}
                className={cn(
                  'rounded-lg px-2.5 py-2',
                  entry.deletedInSource ? 'bg-warning/10' : 'bg-grey-50',
                )}
              >
                <p className="flex items-center justify-between gap-2 text-caption text-grey-500">
                  {entry.deletedInSource ? (
                    <span className="flex items-center gap-1 font-semibold text-status-partial">
                      <AlertTriangle className="h-3 w-3" aria-hidden /> Deleted in Harvest
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Lock className="h-3 w-3" aria-hidden /> Billed
                    </span>
                  )}
                  <span
                    className={cn(
                      'num font-semibold',
                      entry.deletedInSource
                        ? 'text-status-partial line-through decoration-status-partial/60'
                        : 'text-grey-600',
                    )}
                  >
                    {formatHoursValue(entry.hours, hoursFormat)}
                  </span>
                </p>
                <p
                  className={cn(
                    'mt-0.5 text-sm',
                    entry.deletedInSource ? 'text-grey-400 line-through' : 'text-secondary',
                  )}
                >
                  {entry.notes || <span className="italic text-grey-400">No note</span>}
                </p>
              </div>
            ))}
            <p className="text-caption text-grey-500">
              {locked.some((e) => e.deletedInSource)
                ? 'Deleted in Harvest — kept here because it may already be invoiced. Review it in Settings → Harvest sync.'
                : 'Billed time is locked. Unlink it from its invoice to edit.'}
            </p>
          </div>
        ) : multi ? (
          <>
            <p className="text-caption text-grey-500">
              {keeping} {keeping === 1 ? 'entry' : 'entries'} on this day — edit, add or remove them
              here.
            </p>
            {drafts.map((draft, index) => (
              <div
                key={draft.id ?? `new-${index}`}
                className={cn(
                  'rounded-lg border p-2',
                  draft.remove ? 'border-error/40 bg-error/5' : 'border-grey-200',
                )}
              >
                <div className="flex items-center gap-2">
                  <input
                    value={draft.hours}
                    inputMode="decimal"
                    aria-label={`Hours for entry ${index + 1}`}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((d, i) => (i === index ? { ...d, hours: e.target.value } : d)),
                      )
                    }
                    onKeyDown={onFieldKeyDown}
                    disabled={draft.remove}
                    className={cn(
                      fieldClass,
                      'num w-20 shrink-0 text-right',
                      draft.remove && 'text-grey-400 line-through',
                    )}
                  />
                  <input
                    autoFocus={index === 0}
                    value={draft.notes}
                    aria-label={`Notes for entry ${index + 1}`}
                    placeholder="What did you work on?"
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((d, i) => (i === index ? { ...d, notes: e.target.value } : d)),
                      )
                    }
                    onKeyDown={onFieldKeyDown}
                    disabled={draft.remove}
                    className={cn(
                      fieldClass,
                      'min-w-0 flex-1',
                      draft.remove && 'text-grey-400 line-through',
                    )}
                  />
                  {/* Marked here, deleted on Save — reversible until then, and
                      the Save button says how many will go. */}
                  <button
                    type="button"
                    aria-label={
                      draft.remove ? `Keep entry ${index + 1}` : `Remove entry ${index + 1}`
                    }
                    title={
                      draft.remove
                        ? 'Keep this entry'
                        : draft.id
                          ? 'Remove this entry when you save'
                          : 'Discard this new entry'
                    }
                    onClick={() =>
                      setDrafts((prev) =>
                        prev[index].id
                          ? prev.map((d, i) => (i === index ? { ...d, remove: !d.remove } : d))
                          : prev.filter((_, i) => i !== index),
                      )
                    }
                    className={cn(
                      'shrink-0 rounded-md p-1.5 transition-colors',
                      draft.remove
                        ? 'text-accent hover:bg-accent/10'
                        : 'text-grey-400 hover:bg-error/10 hover:text-error',
                    )}
                  >
                    {draft.remove ? (
                      <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    )}
                  </button>
                </div>
                {/* Per entry, because billability is per entry: one day can
                    hold two hours of client work and one of internal time. */}
                <label className="mt-1.5 flex w-fit cursor-pointer items-center gap-2 text-caption font-medium text-secondary">
                  <input
                    type="checkbox"
                    checked={draft.billable}
                    aria-label={`Billable — entry ${index + 1}`}
                    onChange={(e) =>
                      setDrafts((prev) =>
                        prev.map((d, i) => (i === index ? { ...d, billable: e.target.checked } : d)),
                      )
                    }
                    className="h-3.5 w-3.5 rounded border-grey-300 text-accent focus:ring-accent/30"
                  />
                  Billable
                </label>
                {canMove &&
                  draft.id &&
                  !draft.remove &&
                  moveSelect(
                    draft.moveTo,
                    (next) =>
                      setDrafts((prev) =>
                        prev.map((d, i) =>
                          i === index
                            ? {
                                ...d,
                                moveTo: next,
                                // A target applies its default; backing out
                                // restores the flag from before the move.
                                billable: next
                                  ? billableDefault(next, d.billable)
                                  : (d.preMoveBillable ?? d.billable),
                                preMoveBillable: next
                                  ? d.moveTo
                                    ? d.preMoveBillable
                                    : d.billable
                                  : null,
                              }
                            : d,
                        ),
                      ),
                    `Move entry ${index + 1} to another agreement`,
                  )}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setDrafts((prev) => [
                  ...prev,
                  {
                  id: null,
                  hours: '',
                  notes: '',
                  billable: rowBillable,
                  remove: false,
                  moveTo: '',
                  preMoveBillable: null,
                },
                ])
              }
              className="flex items-center gap-1 rounded-md px-1 py-0.5 text-caption font-semibold text-accent transition-colors hover:bg-accent/10"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden /> Add entry
            </button>
            {locked.length > 0 && (
              <p className="flex items-center gap-1 text-caption text-grey-500">
                <Lock className="h-3 w-3 shrink-0" aria-hidden />
                {formatHoursValue(lockedHours, hoursFormat)} more is billed and locked.
              </p>
            )}
          </>
        ) : (
          <>
            <div className="flex items-start gap-2">
              <div className="w-24 shrink-0">
                <label htmlFor="cell-note-hours" className="label mb-1 block">
                  Hours
                </label>
                <input
                  id="cell-note-hours"
                  value={hoursText}
                  inputMode="decimal"
                  // The hint follows the sheet's format toggle. A "1:30" ghost
                  // under a sheet set to 1.50 contradicts every figure around it.
                  placeholder={hoursFormat === 'hm' ? '1:30' : '1.50'}
                  onChange={(e) => setHoursText(e.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void save();
                    }
                  }}
                  className={cn(fieldClass, 'num text-right')}
                />
              </div>
              <label className="flex flex-1 cursor-pointer items-center gap-2 pt-6 text-sm font-medium text-secondary">
                <input
                  type="checkbox"
                  checked={billable}
                  onChange={(e) => setBillable(e.target.checked)}
                  className="h-4 w-4 rounded border-grey-300 text-accent focus:ring-accent/30"
                />
                Billable
              </label>
            </div>
            <div>
              <label htmlFor="cell-note-text" className="label mb-1 block">
                Notes
              </label>
              <textarea
                id="cell-note-text"
                autoFocus
                rows={3}
                value={notes}
                placeholder="What did you work on?"
                onChange={(e) => setNotes(e.target.value)}
                onKeyDown={onFieldKeyDown}
                className={cn(fieldClass, 'resize-none')}
              />
            </div>
            {/* Logged on the wrong project: move the entry, note and all. */}
            {canMove && single && (
              <div>
                <label className="label mb-0 block">Agreement</label>
                {moveSelect(
                  moveTo,
                  (next) => {
                    if (next) {
                      // First target picked: remember what it was. Switching
                      // between targets keeps that first value.
                      if (!moveTo) setPreMoveBillable(billable);
                      setBillable(billableDefault(next, billable));
                    } else if (moveTo) {
                      // Backing out of the move undoes all of it.
                      setBillable(preMoveBillable ?? single?.billable ?? rowBillable);
                      setPreMoveBillable(null);
                    }
                    setMoveTo(next);
                  },
                  'Move this entry to another agreement',
                )}
              </div>
            )}
            {lockedHours > 0 && (
              <p className="flex items-center gap-1 text-caption text-grey-500">
                <Lock className="h-3 w-3 shrink-0" aria-hidden />
                {formatHoursValue(lockedHours, hoursFormat)} of this cell is billed and stays.
              </p>
            )}
          </>
        )}

        {error && (
          <p role="alert" className="flex items-start gap-1.5 text-caption font-semibold text-error">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden /> {error}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-grey-100 px-3 py-2">
        <button
          type="button"
          onClick={() => onOpenDay(target.date)}
          className="flex items-center gap-1 text-caption font-semibold text-grey-500 transition-colors hover:text-accent"
        >
          Open day <ArrowUpRight className="h-3 w-3" aria-hidden />
        </button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {!readOnly && (
            <Button
              size="sm"
              onClick={save}
              loading={saving}
              variant={removing > 0 ? 'destructive' : 'primary'}
              title={
                removing > 0
                  ? `Saves this cell and deletes ${removing} ${removing === 1 ? 'entry' : 'entries'}`
                  : undefined
              }
            >
              {removing > 0 ? `Save · remove ${removing}` : 'Save'}
            </Button>
          )}
        </div>
      </div>
      {/* ⚠ NEVER ADVERTISE A KEY THAT DOES NOTHING. On a billed / deleted-in-
          Harvest cell there is no Save button and nothing to save, and this
          line still promised ⌘/Ctrl+Enter — the precise failure the read-only
          week grid was fixed for, pointed at a different state. */}
      <p className="hidden border-t border-grey-100 px-3 py-1 text-caption text-grey-400 sm:block">
        {readOnly ? 'Esc to close' : '⌘/Ctrl+Enter to save · Esc to cancel'}
      </p>
    </div>
  );
}
